import React, { useEffect, useMemo, useState } from 'react';
import { BsCheckLg, BsPencil, BsTrash, BsXLg } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Text,
  Tooltip,
  useEditableControls,
  VStack
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { portalApi, requirementTemplateApi } from '../../../../../api';
import InsuranceDocumentBlock from '../../../../../components/RequirementBlocks/InsuranceDocumentBlock';
import InsuranceLanguageBlock from '../../../../../components/RequirementBlocks/InsuranceLanguageBlock';
import QuestionBlock from '../../../../../components/RequirementBlocks/QuestionBlock';
import LoadingSpinner from '../../../../../components/Spinner';
import { CypressIds } from '../../../../../constants/cypressIds';
import nothingHereImage from '../../../../../nothing-here.png';
import { createErrorToast, createSuccessToast } from '../../../../../redux/modules/app';
import { hideModal } from '../../../../../redux/modules/modal';
import { hideTray } from '../../../../../redux/modules/tray';
import { globalRequirementTemplateBuilder } from '../../../../../redux/reducer';
import colors from '../../../../../styles/theme/foundation/colors';
import { get } from '../../../../../utils/arrayUtil';
import InsurancePolicyTemplateBlock
  from '../../../RequirementsTemplates/panels/TemplateListPanel/TemplatePreviewTray/TemplateBlocks/InsurancePolicyTemplateBlock';

import PolicyBlockEditor from './PolicyBlockEditor';

const EXTRACTED_CONTRACTS_FOLDER_NAME = 'Extracted Contracts';

const findExtractedContractsFolder = async companyId => {
  let page = 1;
  let lastPage = 1;

  do {
    // eslint-disable-next-line no-await-in-loop
    const response = await requirementTemplateApi.listRequirementTemplateFolders(companyId, { page, pageSize: 100 });
    const folder = (response.data ?? []).find(f => f.name === EXTRACTED_CONTRACTS_FOLDER_NAME);

    if (folder) return folder;

    lastPage = response.last_page ?? response.lastPage ?? 1;
    page += 1;
  } while (page <= lastPage);

  return null;
};

const EmptyTemplateMessage = () => (
  <Flex alignItems="center" direction="column" justifyContent="center" py={12} width="100%">
    <Image src={nothingHereImage} width="40%" />
    <VStack mt={4}>
      <Text textStyle="bodyLg500">Nothing Here</Text>
      <Text textStyle="bodyMd500">All extracted requirements have been removed.</Text>
    </VStack>
  </Flex>
);

const TemplateNameEditableControls = () => {
  const {
    isEditing,
    getSubmitButtonProps,
    getCancelButtonProps,
    getEditButtonProps
  } = useEditableControls();

  if (isEditing) {
    return (
      <HStack spacing={1}>
        <IconButton aria-label="Save template name" icon={<Icon as={BsCheckLg} />} size="xs" variant="textGrey" {...getSubmitButtonProps()} />
        <IconButton aria-label="Cancel template name edit" icon={<Icon as={BsXLg} />} size="xs" variant="textGrey" {...getCancelButtonProps()} />
      </HStack>
    );
  }

  return (
    <Tooltip label="Edit template name" placement="top">
      <IconButton aria-label="Edit template name" icon={<Icon as={BsPencil} />} size="xs" variant="textGrey" {...getEditButtonProps()} />
    </Tooltip>
  );
};

const DeletableBlock = ({ label, onDelete, onEdit = null, children }) => (
  <Flex alignItems="flex-start">
    <Box flex="1" minW={0}>
      {children}
    </Box>
    <VStack ml={2} mt={3} spacing={1}>
      {onEdit && (
        <Tooltip label="Edit policy requirement" placement="top">
          <IconButton
            aria-label="Edit policy requirement"
            icon={<Icon as={BsPencil} />}
            size="xs"
            variant="textGrey"
            onClick={onEdit}
          />
        </Tooltip>
      )}
      <Tooltip label={label} placement="top">
        <IconButton
          aria-label={label}
          icon={<Icon as={BsTrash} />}
          size="xs"
          variant="textGrey"
          onClick={onDelete}
        />
      </Tooltip>
    </VStack>
  </Flex>
);

DeletableBlock.propTypes = {
  label: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  children: PropTypes.node.isRequired
};

const ExtractedTemplateReviewModal = ({ templateId, onUseTemplate }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const template = useSelector(globalRequirementTemplateBuilder.selectCurrentRequirementTemplate);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [templateName, setTemplateName] = useState(null);
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  useEffect(() => {
    dispatch(globalRequirementTemplateBuilder.resetSelectedTemplate());
    dispatch(globalRequirementTemplateBuilder.getRequirementTemplate(templateId));
  }, [dispatch, templateId]);

  const templateQuestions = useMemo(() => {
    if (!template) return [];

    const questionsFromBlocks = template.requirementTemplateBlocks.reduce((acc, block) => {
      if (block.requirementTemplateQuestions) {
        acc.push(...block.requirementTemplateQuestions);
      }

      return acc;
    }, []);

    return [...new Map(questionsFromBlocks.map(q => [q.id, q])).values()];
  }, [template]);

  if (!template || `${template.id}` !== `${templateId}`) {
    return (
      <Box height="300px">
        <LoadingSpinner isInline minHeight="100%" />
      </Box>
    );
  }

  const handleDeleteBlock = block => {
    dispatch(globalRequirementTemplateBuilder.deleteRequirementTemplateBlock(block.id));
  };

  const handleDeleteQuestion = question => {
    dispatch(globalRequirementTemplateBuilder.deleteQuestionRequirement(question.questionId ?? question.question?.id));
  };

  const handleUseTemplate = async () => {
    setIsSaving(true);

    const finalTemplateName = templateName ?? template.name;

    try {
      if (saveToLibrary) {
        let folder = await findExtractedContractsFolder(template.companyId);

        if (!folder) {
          folder = await dispatch(globalRequirementTemplateBuilder.createRequirementTemplateFolder({ name: EXTRACTED_CONTRACTS_FOLDER_NAME }));
        }

        await dispatch(globalRequirementTemplateBuilder.updateRequirementTemplate(template.id, {
          name: finalTemplateName,
          requirement_template_folder_id: folder?.id,
          published_at: template.publishedAt ?? new Date(),
          updated_at: new Date()
        }));

        dispatch(createSuccessToast({
          title: `Template published to "${EXTRACTED_CONTRACTS_FOLDER_NAME} Folder"`,
          duration: 3000
        }));
      } else if (finalTemplateName !== template.name) {
        await dispatch(globalRequirementTemplateBuilder.updateRequirementTemplate(template.id, {
          name: finalTemplateName,
          updated_at: new Date()
        }));
      }

      onUseTemplate({ ...template, name: finalTemplateName });
    } catch (error) {
      setIsSaving(false);
      dispatch(createErrorToast({
        title: 'Something went wrong publishing the template.',
        error
      }));
    }
  };

  const handleEditInTemplateEditor = async () => {
    setIsSaving(true);

    const finalTemplateName = templateName ?? template.name;

    // persist any name edit so it isn't lost when the builder loads from the API
    if (finalTemplateName !== template.name) {
      await dispatch(globalRequirementTemplateBuilder.updateRequirementTemplate(template.id, {
        name: finalTemplateName,
        updated_at: new Date()
      }));
    }

    dispatch(hideModal());
    dispatch(hideTray());
    history.push(`/companies/${template.companyId}/create-templates/requirement-templates/${template.id}`);
  };

  const handleDiscard = async () => {
    setIsDiscarding(true);

    try {
      await portalApi.discardExtractedRequirementTemplate(template.id);
    } catch (error) {
      if (error.status !== 409) {
        dispatch(createErrorToast({
          title: 'The extracted template could not be discarded.',
          error
        }));
      }
    }

    dispatch(hideModal());
  };

  const hasRequirements = !!template.requirementTemplateBlocks?.length;

  return (
    <Box>
      <Text pb={4} textStyle="bodyLg500">
        PINS extracted the requirements below from your contract into a new template.
        Remove anything that doesn&apos;t apply, then use the template for your record(s).
      </Text>

      <Box mb={4}>
        <Text color="greyScale.500" textAlign="center" textStyle="bodySm500">Template Name</Text>
        <Editable
          isPreviewFocusable={false}
          submitOnBlur={false}
          textStyle="bodyLg600"
          value={templateName ?? template.name}
          onCancel={previousValue => setTemplateName(previousValue)}
          onChange={setTemplateName}
          onSubmit={value => setTemplateName(value.trim() || template.name)}
        >
          <HStack justifyContent="center">
            <EditablePreview />
            <EditableInput maxLength={191} textAlign="center" />
            <TemplateNameEditableControls />
          </HStack>
        </Editable>
      </Box>

      <Box maxH="55vh" overflowY="auto" pr={2}>
        {hasRequirements
          ? (
            <>
              {template.requirementTemplateBlocks.map(block => {
                if (!block.requirementTemplateBlockProperties[0]) return null;
                const { requirementType } = block.requirementTemplateBlockProperties[0];

                if (requirementType === 'insurance_policy_type_property') {
                  if (editingBlockId === block.id) {
                    return (
                      <PolicyBlockEditor
                        block={block}
                        key={block.id}
                        onDone={() => setEditingBlockId(null)}
                      />
                    );
                  }

                  return (
                    <DeletableBlock
                      key={block.id}
                      label="Remove policy requirement"
                      onDelete={() => handleDeleteBlock(block)}
                      onEdit={() => setEditingBlockId(block.id)}
                    >
                      <InsurancePolicyTemplateBlock requirementBlock={block} />
                    </DeletableBlock>
                  );
                }

                if (requirementType === 'insurance_document_type') {
                  return (
                    <DeletableBlock key={block.id} label="Remove document requirement" onDelete={() => handleDeleteBlock(block)}>
                      <InsuranceDocumentBlock
                        description={get(block, 'requirementTemplateBlockProperties.0.requirement.description', '')}
                        displayName={get(block, 'requirementTemplateBlockProperties.0.requirement.displayName', '')}
                        isRequired={block.required}
                      />
                    </DeletableBlock>
                  );
                }

                return (
                  <DeletableBlock key={block.id} label="Remove language requirement" onDelete={() => handleDeleteBlock(block)}>
                    <InsuranceLanguageBlock
                      description={get(block, 'requirementTemplateBlockProperties.0.requirement.language', '')}
                      displayName={get(block, 'requirementTemplateBlockProperties.0.requirement.displayName', '')}
                      isRequired={block.required}
                    />
                  </DeletableBlock>
                );
              })}
              {templateQuestions.map(question => (
                <DeletableBlock key={question.id} label="Remove question" onDelete={() => handleDeleteQuestion(question)}>
                  <QuestionBlock question={question.question} />
                </DeletableBlock>
              ))}
            </>
          )
          : <EmptyTemplateMessage />}
      </Box>

      {isConfirmingCancel ? (
        <Flex alignItems="center" flexWrap="wrap" gap={4} justifyContent="space-between" mt={4}>
          <Text color={colors.greyScale['800']} flex="1 1 240px">
            Discard this template? The extracted requirements and uploaded contract will be permanently deleted and no records will be created.
          </Text>

          <HStack>
            <Button
              data-cy={CypressIds.extractedTemplateKeepEditingButton}
              isDisabled={isDiscarding}
              variant="secondary"
              onClick={() => setIsConfirmingCancel(false)}
            >
              Keep Editing
            </Button>
            <Button
              data-cy={CypressIds.extractedTemplateConfirmDiscardButton}
              isLoading={isDiscarding}
              variant="secondaryError"
              onClick={handleDiscard}
            >
              Yes, Discard
            </Button>
          </HStack>
        </Flex>
      ) : (
        <>
          <Flex justifyContent="flex-end" mt={4}>
            <Checkbox
              data-cy={CypressIds.extractedTemplateSaveToLibraryCheckbox}
              isChecked={saveToLibrary}
              isDisabled={isSaving}
              onChange={e => setSaveToLibrary(e.target.checked)}
            >
              <Text textStyle="bodyMd500">Also save to my template library</Text>
            </Checkbox>
          </Flex>

          <Flex alignItems="flex-start" justifyContent="space-between" mt={2}>
            <VStack align="flex-start" spacing={0}>
              <Button
                data-cy={CypressIds.extractedTemplateEditInBuilderButton}
                isDisabled={!hasRequirements || isSaving}
                variant="textPrimary"
                onClick={handleEditInTemplateEditor}
              >
                Edit in Template Editor
              </Button>
              <Text color="greyScale.500" pl={4} textStyle="bodyXs">
                You&apos;ll lose your progress on this page and no records will be created.
              </Text>
            </VStack>
            <HStack>
              <Button
                data-cy={CypressIds.extractedTemplateDiscardButton}
                isDisabled={isSaving}
                variant="secondary"
                onClick={() => setIsConfirmingCancel(true)}
              >
                Cancel
              </Button>
              <Button
                data-cy={CypressIds.extractedTemplateUseTemplateButton}
                isDisabled={!hasRequirements}
                isLoading={isSaving}
                onClick={handleUseTemplate}
              >
                Use Template
              </Button>
            </HStack>
          </Flex>
        </>
      )}
    </Box>
  );
};

ExtractedTemplateReviewModal.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onUseTemplate: PropTypes.func.isRequired
};

export default ExtractedTemplateReviewModal;
